import React, { useEffect, useMemo, useRef, useState } from 'react';
import CusPageContainer from '../../../components/shared/CusPageContainer';
import { useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import Grid from '@mui/material/Grid2';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import { acceptImg, ENUM_SLIDER_LINK, options0To100 } from '../../../enums/enums';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CusInput from '../../../components/material-ui/CusInput';
import TimeZone from '../../../components/shared/TimeZone';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import CusButton from '../../../components/material-ui/CusButton';
import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import CusTextarea from '../../../components/material-ui/CusTextarea.jsx';
import useGroupByGroupId from '../../../hooks/site/useGroupByGroupId.js';
import CusLink from '../../../components/material-ui/CusLink.jsx';
import useEditSliderBanner from '../../../hooks/site/bonus/useEditSliderBanner.js';
import useColor from '../../../hooks/site/useColor.js';
import CusAutocomplete from '../../../components/material-ui/CusAutocomplete.jsx';
import useBootstrapSize from '../../../hooks/useBootstrapSize.js';
import CusUploadFile from '../../../components/material-ui/CusUploadFile.jsx';
import AnchorsLink from '../affiliate_links/referral/AnchorsLink.jsx';
import useToast from '../../../hooks/useToast.js';
import TypographyText from '../../../components/material-ui/table/TypographyText.jsx';
import { API_GROUP } from '../../../api/user.js';

const linkSelectionOptions = [
  { label: 'No link', value: ENUM_SLIDER_LINK.not },
  { label: "Link to bot (with player's ref. code)", value: ENUM_SLIDER_LINK.bot },
  { label: 'Launching the game', value: ENUM_SLIDER_LINK.game },
  { label: 'External URL', value: ENUM_SLIDER_LINK.external },
  { label: 'Relative URL', value: ENUM_SLIDER_LINK.relative },
  { label: 'relative URL with banner description', value: ENUM_SLIDER_LINK.relativeBanner }
];

export default function EditBanner() {
  const { bannerId } = useParams();
  const { infoToast } = useToast();

  const { group, groupId } = useGroupByGroupId();
  const { t } = useTranslation();
  const { action } = useColor();
  const { isMobile } = useBootstrapSize();

  const [gamesOptions, setGamesOptions] = useState([]);

  const BANNER_PREFIX = '/banner/';

  const { currentBanner, days, title, bannerTitle, tgBot, setCurrentBanner, saveBanner } =
    useEditSliderBanner({
      groupId,
      bannerId: bannerId,
      group
    });

  const [link, setLink] = useState(ENUM_SLIDER_LINK.not);
  const [isCreating, setIsCreating] = useState(false);

  const languageOptions = group.langList?.map((el) => ({ label: el, value: el }));

  const activated = currentBanner?.dateFrom;
  const expired = currentBanner?.dateTo;
  const enabled = currentBanner?.enabled;
  const showDays = currentBanner?.weekDays || [];
  const currentBannerId = currentBanner?.id;
  const allowedFormats = acceptImg + ',video/mp4,video/avi,video/webm';

  const urlExample =
    link == ENUM_SLIDER_LINK.relativeBanner
      ? 'some-name'
      : link == ENUM_SLIDER_LINK.relative
        ? '/some-relative-path'
        : ' https://domain.tld/some-url';

  const searchTmRef = useRef(null);

  const changeField = (name, value) => {
    setCurrentBanner((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (currentBanner.makeBotLink) {
      setLink(ENUM_SLIDER_LINK.bot);
      return;
    }
    if (currentBanner.gameId) {
      setLink(ENUM_SLIDER_LINK.game);
      const gameName = currentBanner?.gameInfo?.name;
      if (gameName) searchGame(gameName);
      return;
    }
    if (currentBanner.externalUrl) {
      setLink(ENUM_SLIDER_LINK.external);
      return;
    }
    if (currentBanner.relativeUrl) {
      if (currentBanner.relativeUrl.includes(BANNER_PREFIX)) {
        setLink(ENUM_SLIDER_LINK.relativeBanner);
        const relativeUrl =
          currentBanner.relativeUrl?.split(BANNER_PREFIX)?.[1] ?? currentBanner.relativeUrl;
        setCurrentBanner((prev) => ({ ...prev, relativeUrl: relativeUrl }));
      }
      return;
    }
  }, [currentBannerId]);

  const onSaveClick = async () => {
    setIsCreating(true);
    const newBanner = { ...currentBanner };
    delete newBanner.gameInfo;

    if (link === ENUM_SLIDER_LINK.bot) newBanner.makeBotLink = true;

    if (newBanner.fileData) {
      newBanner.fileData = newBanner.fileData.split('base64,')[1];
    } else {
      newBanner.fileName = '';
    }

    if (newBanner.mobileFileData) {
      newBanner.mobileFileData = newBanner.mobileFileData.split('base64,')[1];
    } else {
      newBanner.mobileFileName = '';
    }

    if (link == ENUM_SLIDER_LINK.relativeBanner) {
      newBanner.relativeUrl = BANNER_PREFIX + newBanner.relativeUrl;
    }

    await saveBanner(newBanner);
    setIsCreating(false);
  };

  const onChangeLink = (val) => {
    setCurrentBanner((prev) => ({
      ...prev,
      makeBotLink: false,
      gameId: 0,
      relativeUrl: '',
      externalUrl: ''
    }));
    setLink(val);
  };

  const searchGame = (value) => {
    if (value.length < 3) return;

    API_GROUP.gameSearch({ groupId, sample: value })
      .then((e) => {
        const list = e.data.list;
        const gameOptions = list.map((el) => ({
          label: `${el.name} (${el.providerName}, id:${el.gameId})`,
          value: el.gameId
        }));
        setGamesOptions(gameOptions);
      })
      .catch(console.error);
  };

  const onChangeAutoCompile = (e) => {
    const value = e.target.value;

    if (!value) setGamesOptions([]);

    clearTimeout(searchTmRef.current);
    searchTmRef.current = setTimeout(() => {
      const value = e.target.value;
      searchGame(value);
    }, 300);
  };

  const hasBanners =
    currentBanner.mobileFileUrl ||
    currentBanner.mobileFileData ||
    currentBanner.fileUrl ||
    currentBanner.fileData;

  const availableBanner = currentBanner.language && hasBanners;

  const valueGameName = useMemo(() => {
    return gamesOptions?.find((el) => el.value == currentBanner.gameId)?.label || '';
  }, [gamesOptions, currentBanner.gameId]);

  return (
    <CusPageContainer title={title} group={group} loader={!group}>
      <CusLink to={`/slider-banners/${groupId}/list`} text="To the list of banners" />

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, md: 'grow' }}>
          <CusCheckbox
            label="Enable"
            checked={enabled}
            onChange={(e) => changeField('enabled', !enabled)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 'auto' }} textAlign={'end'} alignSelf={'end'}>
          <TimeZone groupId={groupId} />
        </Grid>
      </Grid>
      <Grid container title={t(bannerTitle)} spacing={1.5}>
        <Grid size={{ xs: 12, sm: 3, md: 4 }}>
          <Typography fontSize={16} fontWeight={600}>
            {t(bannerTitle)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4.5, md: 4 }} alignSelf={'end'} order={{ xs: 2 }}>
          <CusDateTimePicker
            label="Show start date"
            value={activated}
            onChange={(e) => setCurrentBanner((prev) => ({ ...prev, dateFrom: e }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4.5, md: 4 }} alignSelf={'end'} order={{ xs: 3, md: 3 }}>
          <CusDateTimePicker
            label="Show end date"
            value={expired}
            onChange={(e) => setCurrentBanner((prev) => ({ ...prev, dateTo: e }))}
            errorText={
              activated && expired && activated > expired
                ? t('End date must be later than start date')
                : ''
            }
          />
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 3, md: 4 }} alignSelf={'center'}>
          <Typography>{t('Show days')}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 9, md: 8 }} gap={2} spacing={2}>
          {days.map((day) => (
            <CusCheckbox
              label={day.label}
              key={day.value}
              checked={showDays[day.value]}
              onChange={() => {
                setCurrentBanner((prev) => {
                  const newFields = JSON.parse(JSON.stringify(prev));
                  if (!newFields.weekDays) newFields.weekDays = [];
                  newFields.weekDays[day.value] = !showDays[day.value];
                  return newFields;
                });
              }}
            />
          ))}
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <CusInput
            label={'Name'}
            value={currentBanner.name}
            onChange={(e) => changeField('name', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 }}>
          <Stack>
            <CusLabelSelect
              label={'Position'}
              value={String(currentBanner.position)}
              onChange={(value) => changeField('position', +value)}
              options={options0To100}
              toolTipTitle="A number specifying the relative order of banners in the general list"
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 4 }}>
          <CusLabelSelect
            label={'Language'}
            options={languageOptions}
            value={currentBanner.language}
            onChange={(value) => changeField('language', value)}
          />
        </Grid>
      </Grid>

      <Grid container alignItems={'end'} spacing={1}>
        <Grid size={{ xs: 6, sm: 4 }}>
          <Stack>
            <CusLabelSelect
              label={'Link selection'}
              options={linkSelectionOptions}
              value={link}
              tooltipBgColor={'background.default'}
              toolTipTitle={
                <Box sx={{ background: (theme) => theme.palette.background.default }}>
                  <AnchorsLink
                    title="You can add anchors to the site's link that control the opening of modal windows"
                    onAnchorClick={(val) => {
                      navigator.clipboard.writeText(val).then((e) => {
                        infoToast('copied to your clipboard');
                      });
                    }}
                  />
                </Box>
              }
              onChange={onChangeLink}
            />
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 8 }}>
          <ShowContainer condition={link === ENUM_SLIDER_LINK.game}>
            <CusAutocomplete
              freeSolo
              label={'Link to the game'}
              placeholder={'Enter the name of the game'}
              isAlwaysOptions
              options={gamesOptions}
              value={valueGameName}
              onChange={(_, choseItem) =>
                setCurrentBanner((prev) => ({ ...prev, gameId: choseItem?.value || '' }))
              }
              textFieldProps={{ onChange: onChangeAutoCompile }}
            />
          </ShowContainer>
          <ShowContainer condition={ENUM_SLIDER_LINK.external == link}>
            <CusInput
              value={currentBanner.externalUrl}
              onChange={(e) =>
                setCurrentBanner((prev) => ({ ...prev, externalUrl: e.target.value }))
              }
              placeholder={urlExample}
            />
          </ShowContainer>
          <ShowContainer
            condition={[ENUM_SLIDER_LINK.relativeBanner, ENUM_SLIDER_LINK.relative].includes(link)}>
            <CusInput
              startAdornment={
                <ShowContainer condition={ENUM_SLIDER_LINK.relativeBanner === link}>
                  <Typography sx={{ textWrap: 'nowrap' }}>{BANNER_PREFIX}</Typography>
                </ShowContainer>
              }
              value={currentBanner.relativeUrl}
              onChange={(e) =>
                setCurrentBanner((prev) => ({ ...prev, relativeUrl: e.target.value }))
              }
              placeholder={urlExample}
            />
          </ShowContainer>

          <ShowContainer condition={link == ENUM_SLIDER_LINK.bot}>
            <Stack display={'flex'} direction={'row'} gap={1}>
              <Typography
                color={action.link}
                textAlign={isMobile ? 'end' : undefined}
                onClick={() => window.open(`https://t.me/${tgBot}`)}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                {tgBot}
              </Typography>
            </Stack>
          </ShowContainer>
        </Grid>
      </Grid>

      <CusTextarea
        size={12}
        label={t('Banner text')}
        value={currentBanner.bannerText}
        height={280}
        onChange={(val) => {
          changeField('bannerText', val);
        }}
      />

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CusUploadFile
            accept={allowedFormats}
            id={'desktopBannerFile'}
            title={'Banner file (850x350 or 1700x400)'}
            fileSource={currentBanner.fileUrl || currentBanner.fileData}
            onChange={(val, name) => {
              changeField('fileUrl', undefined);
              changeField('fileData', val);
              changeField('fileName', name);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CusUploadFile
            accept={allowedFormats}
            id={'mobileBannerFile'}
            title={'Mobile banner file (720x480 or 850x350)'}
            fileSource={currentBanner.mobileFileUrl || currentBanner.mobileFileData}
            onChange={(val, name) => {
              changeField('mobileFileUrl', undefined);
              changeField('mobileFileData', val);
              changeField('mobileFileName', name);
            }}
          />
        </Grid>
      </Grid>

      <CusButton
        text={'Save'}
        fullWidth
        boxProps={{ mt: 2 }}
        onClick={onSaveClick}
        disabled={!availableBanner || isCreating}
      />
    </CusPageContainer>
  );
}
